import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { onAuthStateChanged, signOut as fbSignOut, type User as FirebaseUser } from 'firebase/auth';
import { firebaseAuth } from '../lib/firebase/init';

/** App-facing user shape — keeps Firebase types out of the component tree. */
export interface AppUser {
  uid: string;
  email: string | null;
  displayName: string | null;
  photoURL: string | null;
}

interface AuthValue {
  user: AppUser | null;
  isLoading: boolean;
  signOut: () => Promise<void>;
}

const AuthCtx = createContext<AuthValue | null>(null);

function toAppUser(u: FirebaseUser): AppUser {
  return { uid: u.uid, email: u.email, displayName: u.displayName, photoURL: u.photoURL };
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AppUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const unsub = onAuthStateChanged(firebaseAuth(), (u) => {
      setUser(u ? toAppUser(u) : null);
      setIsLoading(false);
    });
    return () => unsub();
  }, []);

  const value = useMemo<AuthValue>(
    () => ({
      user,
      isLoading,
      signOut: () => fbSignOut(firebaseAuth()),
    }),
    [user, isLoading],
  );

  return <AuthCtx.Provider value={value}>{children}</AuthCtx.Provider>;
}

export function useAuth(): AuthValue {
  const v = useContext(AuthCtx);
  if (!v) throw new Error('AuthProvider not mounted');
  return v;
}
